import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Mail, Lock, User, Eye, EyeOff, ArrowRight, Phone, MapPin,
  CheckCircle, AlertCircle, Loader2, GraduationCap,
  ShieldCheck, Camera, ScanLine, ArrowLeft, Home, Globe, KeyRound,
  Building2, Calendar, Ticket
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_PLACES_API_KEY = 'AIzaSyDUuY2IMiThTb8Yb0Qpx22CXYlCl5_fiW0';

// Load Google Places script
const loadGooglePlaces = () => {
  return new Promise((resolve) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve(window.google.maps.places);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => resolve(window.google.maps.places);
    document.head.appendChild(script);
  });
};

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const verified = searchParams.get('verified');
  const reason = searchParams.get('reason');
  
  const [isLogin, setIsLogin] = useState(mode !== 'register');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inactivityWarning, setInactivityWarning] = useState(reason === 'inactivity');
  
  // Registration steps:
  // 0 = basic info (name, phone, email, password)
  // 1 = email verification code
  // 2 = address
  // 3 = KYC verification
  // 4 = polling/waiting
  // 5 = complete
  const [regStep, setRegStep] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [verificationUrl, setVerificationUrl] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [pollingStatus, setPollingStatus] = useState('pending');
  const [customerId, setCustomerId] = useState(null);
  
  // Login MFA state
  const [loginStep, setLoginStep] = useState(0); // 0 = credentials, 1 = MFA code
  const [mfaEmail, setMfaEmail] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaCountdown, setMfaCountdown] = useState(0);
  
  // Signup email verification
  const [signupCode, setSignupCode] = useState('');
  const [signupCodeSent, setSignupCodeSent] = useState(false);
  const [signupCountdown, setSignupCountdown] = useState(0);
  
  // Address autocomplete
  const [autocomplete, setAutocomplete] = useState(null);
  
  // Already verified in-store flow
  const [signupMethod, setSignupMethod] = useState(null); // null = choose, 'new' or 'verified'
  const [storeCode, setStoreCode] = useState('');
  const [storeVerified, setStoreVerified] = useState(false);
  const [storeCustomerId, setStoreCustomerId] = useState(null);
  const [dateOfBirth, setDateOfBirth] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize Google Places
  useEffect(() => {
    if (regStep === 2) {
      loadGooglePlaces().then(() => {
        const input = document.getElementById('address-autocomplete');
        if (input && window.google) {
          const ac = new window.google.maps.places.Autocomplete(input, {
            types: ['address'],
            componentRestrictions: { country: ['ca', 'us'] }
          });
          ac.addListener('place_changed', () => {
            const place = ac.getPlace();
            if (place.address_components) {
              const getComponent = (type) => {
                const comp = place.address_components.find(c => c.types.includes(type));
                return comp ? comp.long_name : '';
              };
              setFormData(prev => ({
                ...prev,
                street: `${getComponent('street_number')} ${getComponent('route')}`.trim(),
                city: getComponent('locality') || getComponent('sublocality'),
                state: getComponent('administrative_area_level_1'),
                postalCode: getComponent('postal_code'),
                country: getComponent('country')
              }));
            }
          });
          setAutocomplete(ac);
        }
      });
    }
  }, [regStep]);
  
  // Countdown timers
  useEffect(() => {
    if (mfaCountdown > 0) {
      const timer = setTimeout(() => setMfaCountdown(mfaCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [mfaCountdown]);
  
  useEffect(() => {
    if (signupCountdown > 0) {
      const timer = setTimeout(() => setSignupCountdown(signupCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [signupCountdown]);
  
  // Poll for KYC status
  useEffect(() => {
    let pollInterval;
    if (regStep === 4 && sessionId) {
      pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/api/kyc/status/${sessionId}`);
          const { status, message, customer_id, address_prefill, inbox_file, token, user } = response.data;
          
          if (status === 'approved') {
            setPollingStatus('approved');
            clearInterval(pollInterval);
            
            // Auto-login if token is provided
            if (token) {
              localStorage.setItem('fonderx_token', token);
              setSuccess('Verification successful! Redirecting to dashboard...');
              setTimeout(() => {
                window.location.href = '/dashboard';
              }, 1500);
            } else {
              setRegStep(5);
              setSuccess(`Verification successful! Your account has been created. File: ${inbox_file || sessionId}`);
            }
          } else if (status === 'needs_address') {
            // This shouldn't happen with new flow, but handle it
            setCustomerId(customer_id);
            if (address_prefill) {
              setFormData(prev => ({
                ...prev,
                street: address_prefill.street || '',
                city: address_prefill.city || '',
                state: address_prefill.state || '',
                postalCode: address_prefill.postal_code || '',
                country: address_prefill.country || ''
              }));
            }
            setRegStep(2);
            clearInterval(pollInterval);
          } else if (status === 'declined') {
            setPollingStatus('declined');
            setError(message || 'Verification failed. Please try again.');
            clearInterval(pollInterval);
          }
        } catch (err) {
          console.error('Status poll error:', err);
        }
      }, 3000);
    }
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [regStep, sessionId]);
  
  // Update isLogin when URL changes
  useEffect(() => {
    setIsLogin(mode !== 'register');
    setRegStep(0);
    setLoginStep(0);
  }, [mode]);
  
  // Show success message if email verified
  useEffect(() => {
    if (verified === 'true') {
      setSuccess('Email verified successfully! You can now log in.');
    }
  }, [verified]);
  
  const from = location.state?.from?.pathname || '/dashboard';

  // Form fields
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    newsletter: true,
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  // Password strength validation
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('at least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('one lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('one number');
    if (!/[!@#$%^&*()_+\-=\[\]{}|;':",./<>?]/.test(password)) errors.push('one special character');
    return errors;
  };

  const getPasswordStrength = (password) => {
    const errors = validatePassword(password);
    if (errors.length === 0) return { strength: 'strong', color: 'bg-emerald-500' };
    if (errors.length <= 2) return { strength: 'medium', color: 'bg-yellow-500' };
    return { strength: 'weak', color: 'bg-red-500' };
  };

  const validateBasicInfo = () => {
    if (!formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return false;
    }
    if (!formData.firstName || !formData.lastName) {
      setError('Please enter your full name');
      return false;
    }
    if (!formData.phone) {
      setError('Please enter your phone number');
      return false;
    }
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      setError(`Password must contain ${passwordErrors.join(', ')}`);
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const validateAddress = () => {
    if (!formData.street || !formData.city || !formData.postalCode || !formData.country) {
      setError('Please fill in your complete address');
      return false;
    }
    return true;
  };

  // Login Step 1: Submit credentials
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/api/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      
      if (response.data.requires_mfa) {
        setMfaEmail(formData.email);
        setLoginStep(1);
        setMfaCountdown(300); // 5 minutes
        setSuccess('Verification code sent to your email');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Login Step 2: Submit MFA code
  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaCode || mfaCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/api/auth/verify-login-code`, {
        email: mfaEmail,
        code: mfaCode
      });
      
      if (response.data.access_token) {
        // Store token and user data - use same key as AuthContext
        localStorage.setItem('fonderx_token', response.data.access_token);
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resend MFA code
  const resendMfaCode = async () => {
    if (mfaCountdown > 240) return; // Can only resend after 1 minute
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/send-login-code`, { email: mfaEmail });
      setMfaCountdown(300);
      setSuccess('New verification code sent');
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Registration Step 0 -> 1: Submit basic info and send verification code
  const handleBasicInfoSubmit = async (e) => {
    e.preventDefault();
    if (!validateBasicInfo()) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Send signup verification code
      await axios.post(`${API}/api/auth/send-signup-code`, {
        email: formData.email,
        first_name: formData.firstName
      });
      
      setSignupCodeSent(true);
      setSignupCountdown(600); // 10 minutes
      setRegStep(1);
      setSuccess('Verification code sent to your email');
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Registration Step 1 -> 2: Verify email code
  const handleSignupCodeSubmit = async (e) => {
    e.preventDefault();
    if (!signupCode || signupCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/api/auth/verify-signup-code`, {
        email: formData.email,
        code: signupCode
      });
      
      setRegStep(2);
      setSuccess('Email verified! Please enter your address.');
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resend signup code
  const resendSignupCode = async () => {
    if (signupCountdown > 540) return; // Can only resend after 1 minute
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/send-signup-code`, {
        email: formData.email,
        first_name: formData.firstName
      });
      setSignupCountdown(600);
      setSuccess('New verification code sent');
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Registration Step 2 -> 3: Submit address and start KYC
  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    if (!validateAddress()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/api/kyc/create-session`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          postal_code: formData.postalCode,
          country: formData.country
        },
        newsletter_subscribed: formData.newsletter
      });
      
      if (response.data.success) {
        setSessionId(response.data.session_id);
        setVerificationUrl(response.data.verification_url);
        setQrCodeUrl(response.data.qr_code_url);
        setRegStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    setRegStep(4);
  };

  const goBack = () => {
    if (signupMethod === 'verified' && !storeVerified) {
      // Go back from store verification to method selection
      setSignupMethod(null);
      setStoreCode('');
      setError('');
      return;
    }
    if (signupMethod === 'verified' && storeVerified && regStep === 1) {
      // Go back from email verification in store flow
      setStoreVerified(false);
      setSignupCode('');
      setError('');
      return;
    }
    if (regStep > 0) setRegStep(regStep - 1);
    if (loginStep > 0) setLoginStep(0);
    setError('');
  };

  // Store verification handlers
  const handleVerifyStoreCode = async (e) => {
    e.preventDefault();
    if (!storeCode || storeCode.length < 6) {
      setError('Please enter a valid verification code');
      return;
    }
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !dateOfBirth) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/api/auth/verify-store-code`, {
        code: storeCode,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        date_of_birth: dateOfBirth,
        phone: formData.phone,
        postal_code: formData.postalCode || null
      });
      
      if (response.data.success) {
        setStoreVerified(true);
        setStoreCustomerId(response.data.customer_id);
        // Send email verification code
        await axios.post(`${API}/api/auth/send-signup-code`, {
          email: formData.email,
          first_name: formData.firstName
        });
        setSignupCodeSent(true);
        setSignupCountdown(600);
        setRegStep(1); // Go to email verification step
        setSuccess('Information verified! Please verify your email.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. Please check your information.');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreSignupComplete = async (e) => {
    e.preventDefault();
    if (!signupCode || signupCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    if (!formData.password) {
      setError('Please create a password');
      return;
    }
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      setError(`Password must contain ${passwordErrors.join(', ')}`);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/api/auth/complete-store-signup`, {
        code: storeCode,
        email: formData.email,
        password: formData.password,
        email_code: signupCode,
        first_name: formData.firstName,
        last_name: formData.lastName
      });
      
      if (response.data.success && response.data.token) {
        localStorage.setItem('fonderx_token', response.data.token);
        setSuccess('Account created successfully! Redirecting...');
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRegStep(0);
    setLoginStep(0);
    setSessionId(null);
    setVerificationUrl(null);
    setQrCodeUrl(null);
    setMfaCode('');
    setSignupCode('');
    setSignupMethod(null);
    setStoreCode('');
    setStoreVerified(false);
    setStoreCustomerId(null);
    setDateOfBirth('');
    setError('');
  };

  // Render signup method selection (Step 0 for registration)
  const renderSignupMethodSelection = () => (
    <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-2xl border border-white/10 p-6 sm:p-8">
      {/* Tab Switcher */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { setIsLogin(true); setError(''); setLoginStep(0); setSignupMethod(null); }}
          className={`flex-1 py-3 rounded-xl font-medium transition-all ${
            isLogin ? 'bg-[#3B82F6] text-white' : 'bg-white/5 text-blue-100/70 hover:bg-white/10'
          }`}
          data-testid="login-tab"
        >
          Log In
        </button>
        <button
          onClick={() => { setIsLogin(false); setError(''); setRegStep(0); setSignupMethod(null); }}
          className={`flex-1 py-3 rounded-xl font-medium transition-all ${
            !isLogin ? 'bg-[#3B82F6] text-white' : 'bg-white/5 text-blue-100/70 hover:bg-white/10'
          }`}
          data-testid="register-tab"
        >
          Sign Up
        </button>
      </div>

      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-white mb-2">Create Account</h2>
        <p className="text-blue-100/60 text-sm">Choose how you'd like to sign up</p>
      </div>

      <div className="space-y-3">
        <button
          onClick={() => setSignupMethod('new')}
          className="w-full p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-white/20 text-left transition-all group"
          data-testid="signup-new-customer"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
              <User className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <div className="font-medium text-white">I'm a new customer</div>
              <div className="text-sm text-blue-100/60">Complete ID verification online</div>
            </div>
          </div>
        </button>
        
        <button
          onClick={() => setSignupMethod('verified')}
          className="w-full p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-white/20 text-left transition-all group"
          data-testid="signup-verified-store"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
              <Building2 className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <div className="font-medium text-white">I was already verified in-store</div>
              <div className="text-sm text-blue-100/60">I have a verification code from the store</div>
            </div>
          </div>
        </button>
      </div>

      {/* Footer Links */}
      <div className="mt-6 pt-6 border-t border-white/10 text-center">
        <p className="text-blue-100/40 text-sm">
          Already have an account?{' '}
          <button
            onClick={() => { setIsLogin(true); setError(''); setSuccess(''); }}
            className="text-[#3B82F6] hover:text-[#60A5FA] font-medium transition-colors"
          >
            Log In
          </button>
        </p>
      </div>
    </div>
  );

  // Render store code verification form
  const renderStoreCodeVerification = () => (
    <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-2xl border border-white/10 p-6 sm:p-8">
      <button onClick={goBack} className="text-blue-100/60 hover:text-white text-sm transition-colors flex items-center gap-2 mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>
      
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Ticket className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Enter Store Verification Code</h2>
        <p className="text-blue-100/60 text-sm">
          Enter your information exactly as it appears in store records
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-lg mb-4">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      <form onSubmit={handleVerifyStoreCode} className="space-y-4">
        {/* Store Code */}
        <div>
          <label className="block text-blue-100/60 text-sm mb-1.5">Verification Code *</label>
          <input
            type="text"
            value={storeCode}
            onChange={(e) => setStoreCode(e.target.value.toUpperCase())}
            placeholder="e.g., ABCD1234"
            maxLength={10}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-center text-xl tracking-widest placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors font-mono"
            data-testid="store-code-input"
            autoFocus
          />
        </div>

        {/* Name */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-blue-100/60 text-sm mb-1.5">First Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="First name"
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
                data-testid="store-firstname"
              />
            </div>
          </div>
          <div>
            <label className="block text-blue-100/60 text-sm mb-1.5">Last Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Last name"
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
                data-testid="store-lastname"
              />
            </div>
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-blue-100/60 text-sm mb-1.5">Email Address *</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="you@example.com"
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="store-email"
            />
          </div>
        </div>

        {/* Date of Birth */}
        <div>
          <label className="block text-blue-100/60 text-sm mb-1.5">Date of Birth *</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="store-dob"
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label className="block text-blue-100/60 text-sm mb-1.5">Phone Number *</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+1 (604) 123-4567"
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="store-phone"
            />
          </div>
        </div>

        {/* Postal Code (optional) */}
        <div>
          <label className="block text-blue-100/60 text-sm mb-1.5">Postal Code <span className="text-blue-100/40">(optional)</span></label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
            <input
              type="text"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleInputChange}
              placeholder="V6B 1A1"
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="store-postal"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="store-verify-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Verifying...
            </>
          ) : (
            <>
              Verify My Information
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>

      {/* Info box */}
      <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <p className="text-blue-100/60 text-xs">
          <span className="text-white font-medium">Tip:</span> Enter your information exactly as it was provided when you visited our store. This includes your name, email, date of birth, and phone number.
        </p>
      </div>
    </div>
  );

  // Render store signup email verification + password creation
  const renderStoreEmailVerification = () => (
    <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-2xl border border-white/10 p-6 sm:p-8">
      <button onClick={goBack} className="text-blue-100/60 hover:text-white text-sm transition-colors flex items-center gap-2 mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>
      
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-medium">1</div>
        <div className="w-12 h-1 bg-[#3B82F6]"></div>
        <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-sm font-medium">2</div>
      </div>
      
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-blue-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Complete Your Account</h2>
        <p className="text-blue-100/60 text-sm">
          Verify your email and create a password
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-lg mb-4">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      <form onSubmit={handleStoreSignupComplete} className="space-y-4">
        {/* Email verification code */}
        <div>
          <label className="block text-blue-100/60 text-sm mb-1.5">
            Email Verification Code *
            <span className="text-blue-100/40 ml-2">
              (sent to {formData.email})
            </span>
          </label>
          <input
            type="text"
            value={signupCode}
            onChange={(e) => setSignupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Enter 6-digit code"
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-center text-xl tracking-[0.5em] placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
            data-testid="store-email-code"
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-blue-100/40 text-xs">
              Code expires in {Math.floor(signupCountdown / 60)}:{String(signupCountdown % 60).padStart(2, '0')}
            </p>
            <button
              type="button"
              onClick={resendSignupCode}
              disabled={signupCountdown > 540}
              className="text-[#3B82F6] hover:text-[#60A5FA] text-xs transition-colors disabled:opacity-30"
            >
              Resend code
            </button>
          </div>
        </div>

        {/* Password */}
        <div>
          <label className="block text-blue-100/60 text-sm mb-1.5">Create Password *</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Create a strong password"
              className="w-full pl-10 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="store-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-100/30 hover:text-blue-100/60 transition-colors"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {formData.password && (
            <div className="mt-2">
              <div className="flex items-center gap-2 mb-1">
                <div className={`h-1 flex-1 rounded ${getPasswordStrength(formData.password).color}`}></div>
                <span className="text-xs text-blue-100/60">{getPasswordStrength(formData.password).strength}</span>
              </div>
              {validatePassword(formData.password).length > 0 && (
                <p className="text-xs text-blue-100/40">
                  Need: {validatePassword(formData.password).join(', ')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-blue-100/60 text-sm mb-1.5">Confirm Password *</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
            <input
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm your password"
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="store-confirm-password"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || signupCode.length !== 6 || !formData.password}
          className="w-full py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="store-complete-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Creating Account...
            </>
          ) : (
            <>
              Create Account
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>

      {/* Success info */}
      <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white text-sm font-medium">Already Verified</p>
            <p className="text-blue-100/60 text-xs mt-1">
              Your identity has been verified in-store. You can skip the online ID verification process and start using your account immediately.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Render different steps
  const renderLoginMFA = () => (
    <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-2xl border border-white/10 p-6 sm:p-8">
      <button onClick={goBack} className="text-blue-100/60 hover:text-white text-sm transition-colors flex items-center gap-2 mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>
      
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <KeyRound className="w-8 h-8 text-blue-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Enter Verification Code</h2>
        <p className="text-blue-100/60 text-sm">
          We sent a 6-digit code to <span className="text-white">{mfaEmail}</span>
        </p>
        <p className="text-blue-100/40 text-xs mt-1">
          Code expires in {Math.floor(mfaCountdown / 60)}:{String(mfaCountdown % 60).padStart(2, '0')}
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-lg mb-4">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      <form onSubmit={handleMfaSubmit} className="space-y-4">
        <div>
          <input
            type="text"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Enter 6-digit code"
            className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white text-center text-2xl tracking-[0.5em] placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
            data-testid="mfa-code"
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={loading || mfaCode.length !== 6}
          className="w-full py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="mfa-submit"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Login'}
        </button>

        <button
          type="button"
          onClick={resendMfaCode}
          disabled={mfaCountdown > 240}
          className="w-full py-2 text-blue-100/60 hover:text-white text-sm transition-colors disabled:opacity-30"
        >
          {mfaCountdown > 240 ? `Resend code in ${mfaCountdown - 240}s` : 'Resend code'}
        </button>
      </form>
    </div>
  );

  const renderSignupEmailVerification = () => (
    <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-2xl border border-white/10 p-6 sm:p-8">
      <button onClick={goBack} className="text-blue-100/60 hover:text-white text-sm transition-colors flex items-center gap-2 mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>
      
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-medium">✓</div>
        <div className="w-12 h-1 bg-[#3B82F6]"></div>
        <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-sm font-medium">2</div>
        <div className="w-12 h-1 bg-white/20"></div>
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/50 text-sm font-medium">3</div>
        <div className="w-12 h-1 bg-white/20"></div>
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/50 text-sm font-medium">4</div>
      </div>
      
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-blue-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Verify Your Email</h2>
        <p className="text-blue-100/60 text-sm">
          We sent a 6-digit code to <span className="text-white">{formData.email}</span>
        </p>
        <p className="text-blue-100/40 text-xs mt-1">
          Code expires in {Math.floor(signupCountdown / 60)}:{String(signupCountdown % 60).padStart(2, '0')}
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-lg mb-4">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      <form onSubmit={handleSignupCodeSubmit} className="space-y-4">
        <div>
          <input
            type="text"
            value={signupCode}
            onChange={(e) => setSignupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Enter 6-digit code"
            className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white text-center text-2xl tracking-[0.5em] placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
            data-testid="signup-code"
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={loading || signupCode.length !== 6}
          className="w-full py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="signup-code-submit"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify Email'}
        </button>

        <button
          type="button"
          onClick={resendSignupCode}
          disabled={signupCountdown > 540}
          className="w-full py-2 text-blue-100/60 hover:text-white text-sm transition-colors disabled:opacity-30"
        >
          {signupCountdown > 540 ? `Resend code in ${signupCountdown - 540}s` : 'Resend code'}
        </button>
      </form>
    </div>
  );

  const renderComplete = () => (
    <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-2xl border border-white/10 p-8 text-center">
      <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
        <ShieldCheck className="w-10 h-10 text-emerald-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-3">Registration Complete!</h2>
      <p className="text-blue-100/60 mb-6">
        Your identity has been verified and your account is ready to use.
      </p>
      <button
        onClick={() => {
          setIsLogin(true);
          resetForm();
          setSuccess('Please log in with your credentials');
        }}
        className="w-full py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-xl transition-colors"
        data-testid="continue-to-login-btn"
      >
        Continue to Login
      </button>
    </div>
  );

  const renderPolling = () => (
    <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-2xl border border-white/10 p-8 text-center">
      <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
        <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
      </div>
      <h2 className="text-xl font-bold text-white mb-3">Verifying Your Identity...</h2>
      <p className="text-blue-100/60 mb-4">
        Please complete the verification in the new tab. This page will update automatically.
      </p>
      <div className="flex items-center justify-center gap-2 text-blue-100/40 text-sm">
        <ScanLine className="w-4 h-4" />
        <span>Waiting for verification...</span>
      </div>
      
      {error && (
        <div className="mt-6 flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      <button onClick={resetForm} className="mt-6 text-blue-100/60 hover:text-white text-sm transition-colors flex items-center gap-2 mx-auto">
        <ArrowLeft className="w-4 h-4" />
        Start Over
      </button>
    </div>
  );

  const renderKYCVerification = () => (
    <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-2xl border border-white/10 p-6 sm:p-8">
      <button onClick={goBack} className="text-blue-100/60 hover:text-white text-sm transition-colors flex items-center gap-2 mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>
      
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-medium">✓</div>
        <div className="w-12 h-1 bg-emerald-500"></div>
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-medium">✓</div>
        <div className="w-12 h-1 bg-emerald-500"></div>
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-medium">✓</div>
        <div className="w-12 h-1 bg-[#3B82F6]"></div>
        <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-sm font-medium">4</div>
      </div>
      
      <div className="text-center">
        <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="w-8 h-8 text-blue-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Verify Your Identity</h2>
        <p className="text-blue-100/60 text-sm mb-6">
          Take a photo of your government ID and a quick selfie to complete verification.
        </p>
        
        {/* Important Notice */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6 text-left">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white text-sm font-medium">Important</p>
              <p className="text-blue-100/60 text-xs mt-1">
                After completing verification, please return to this page. It will automatically update with your results.
              </p>
            </div>
          </div>
        </div>
        
        {/* What you need box */}
        <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
          <h3 className="text-white font-medium mb-3 text-sm">What you'll need:</h3>
          <ul className="space-y-2 text-blue-100/60 text-sm">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              Government-issued ID (passport, driver's license)
            </li>
            <li className="flex items-center gap-2">
              <Camera className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              Camera access for photos
            </li>
            <li className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              Good lighting for clear photos
            </li>
          </ul>
        </div>

        {/* Primary CTA */}
        <a
          href={verificationUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setTimeout(() => setRegStep(4), 500)}
          className="block w-full py-4 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-xl transition-colors text-center mb-6"
          data-testid="start-verification-btn"
        >
          <span className="flex items-center justify-center gap-2">
            <Camera className="w-5 h-5" />
            Start Verification
          </span>
        </a>
        
        {/* QR Code - Secondary Option */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
          <p className="text-blue-100/50 text-xs mb-4 uppercase tracking-wider">Or use your phone</p>
          <div className="bg-white p-3 rounded-xl inline-block mb-4">
            {qrCodeUrl ? (
              <img src={qrCodeUrl} alt="Verification QR Code" className="w-28 h-28" data-testid="kyc-qr-code" />
            ) : (
              <div className="w-28 h-28 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            )}
          </div>
          <p className="text-blue-100/40 text-xs">Scan with your phone camera</p>
          <button onClick={startPolling} className="mt-3 text-[#3B82F6] hover:text-[#60A5FA] text-sm transition-colors">
            I've scanned the QR code →
          </button>
        </div>
      </div>
    </div>
  );

  const renderAddressForm = () => (
    <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-2xl border border-white/10 p-6 sm:p-8">
      <button onClick={goBack} className="text-blue-100/60 hover:text-white text-sm transition-colors flex items-center gap-2 mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>
      
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-medium">✓</div>
        <div className="w-12 h-1 bg-emerald-500"></div>
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-medium">✓</div>
        <div className="w-12 h-1 bg-[#3B82F6]"></div>
        <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-sm font-medium">3</div>
        <div className="w-12 h-1 bg-white/20"></div>
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/50 text-sm font-medium">4</div>
      </div>
      
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <MapPin className="w-8 h-8 text-blue-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Your Address</h2>
        <p className="text-blue-100/60 text-sm">
          Please enter your current residential address.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <form onSubmit={handleAddressSubmit} className="space-y-4">
        {/* Street Address with Google Autocomplete */}
        <div>
          <label className="block text-blue-100/60 text-sm mb-1.5">Street Address *</label>
          <div className="relative">
            <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
            <input
              id="address-autocomplete"
              type="text"
              name="street"
              value={formData.street}
              onChange={handleInputChange}
              placeholder="Start typing your address..."
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="address-street"
            />
          </div>
        </div>

        {/* City and State/Province */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-blue-100/60 text-sm mb-1.5">City *</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder="Vancouver"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="address-city"
            />
          </div>
          <div>
            <label className="block text-blue-100/60 text-sm mb-1.5">State/Province</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              placeholder="BC"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="address-state"
            />
          </div>
        </div>

        {/* Postal Code and Country */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-blue-100/60 text-sm mb-1.5">Postal/Zip Code *</label>
            <input
              type="text"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleInputChange}
              placeholder="V6B 1A1"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="address-postal"
            />
          </div>
          <div>
            <label className="block text-blue-100/60 text-sm mb-1.5">Country *</label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="Canada"
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
                data-testid="address-country"
              />
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white text-sm font-medium">Why do we need this?</p>
              <p className="text-blue-100/60 text-xs mt-1">
                FINTRAC regulations require us to collect and verify customer addresses for all currency exchange services.
              </p>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="address-submit-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              Continue to Verification
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>
    </div>
  );

  const renderBasicInfo = () => (
    <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-2xl border border-white/10 p-6 sm:p-8">
      {/* Tab Switcher */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { setIsLogin(true); setError(''); setLoginStep(0); }}
          className={`flex-1 py-3 rounded-xl font-medium transition-all ${
            isLogin ? 'bg-[#3B82F6] text-white' : 'bg-white/5 text-blue-100/70 hover:bg-white/10'
          }`}
          data-testid="login-tab"
        >
          Log In
        </button>
        <button
          onClick={() => { setIsLogin(false); setError(''); setRegStep(0); }}
          className={`flex-1 py-3 rounded-xl font-medium transition-all ${
            !isLogin ? 'bg-[#3B82F6] text-white' : 'bg-white/5 text-blue-100/70 hover:bg-white/10'
          }`}
          data-testid="register-tab"
        >
          Sign Up
        </button>
      </div>

      {/* Progress indicator for registration */}
      {!isLogin && (
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-sm font-medium">1</div>
          <div className="w-12 h-1 bg-white/20"></div>
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/50 text-sm font-medium">2</div>
          <div className="w-12 h-1 bg-white/20"></div>
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/50 text-sm font-medium">3</div>
          <div className="w-12 h-1 bg-white/20"></div>
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/50 text-sm font-medium">4</div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-lg mb-4">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      <form onSubmit={isLogin ? handleLoginSubmit : handleBasicInfoSubmit} className="space-y-4">
        {/* Registration Fields */}
        {!isLogin && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-blue-100/60 text-sm mb-1.5">First Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="First name"
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
                    data-testid="register-firstname"
                  />
                </div>
              </div>
              <div>
                <label className="block text-blue-100/60 text-sm mb-1.5">Last Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Last name"
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
                    data-testid="register-lastname"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-blue-100/60 text-sm mb-1.5">Phone Number *</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (604) 123-4567"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
                  data-testid="register-phone"
                />
              </div>
            </div>
          </>
        )}

        {/* Email */}
        <div>
          <label className="block text-blue-100/60 text-sm mb-1.5">Email Address *</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="you@example.com"
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="auth-email"
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label className="block text-blue-100/60 text-sm mb-1.5">Password *</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="••••••••"
              className="w-full pl-10 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="auth-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-100/30 hover:text-blue-100/60 transition-colors"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {/* Password strength indicator for registration */}
          {!isLogin && formData.password && (
            <div className="mt-2">
              <div className="flex items-center gap-2 mb-1">
                <div className={`h-1 flex-1 rounded ${getPasswordStrength(formData.password).color}`}></div>
                <span className="text-xs text-blue-100/60">{getPasswordStrength(formData.password).strength}</span>
              </div>
              {validatePassword(formData.password).length > 0 && (
                <p className="text-xs text-blue-100/40">
                  Need: {validatePassword(formData.password).join(', ')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Confirm Password (Register only) */}
        {!isLogin && (
          <div>
            <label className="block text-blue-100/60 text-sm mb-1.5">Confirm Password *</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-100/30" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="••••••••"
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6] transition-colors"
                data-testid="register-confirm-password"
              />
            </div>
          </div>
        )}

        {/* Newsletter (Register only) */}
        {!isLogin && (
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="newsletter"
              checked={formData.newsletter}
              onChange={handleInputChange}
              className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-[#3B82F6] focus:ring-[#3B82F6]"
            />
            <span className="text-blue-100/60 text-sm">
              Subscribe to rate updates and promotions
            </span>
          </label>
        )}

        {/* Security Notice (Register only) */}
        {!isLogin && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-sm font-medium">Secure Registration</p>
                <p className="text-blue-100/60 text-xs mt-1">
                  You'll verify your email, provide your address, and complete ID verification. MFA is required for all logins.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="submit-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {isLogin ? 'Logging in...' : 'Processing...'}
            </>
          ) : (
            <>
              {isLogin ? 'Log In' : 'Continue'}
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        {/* Forgot Password (Login only) */}
        {isLogin && (
          <div className="text-center">
            <Link to="/forgot-password" className="text-blue-100/60 hover:text-white text-sm transition-colors">
              Forgot your password?
            </Link>
          </div>
        )}
      </form>

      {/* Footer Links */}
      <div className="mt-6 pt-6 border-t border-white/10 text-center">
        <p className="text-blue-100/40 text-sm">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button
            onClick={() => { setIsLogin(!isLogin); setError(''); setSuccess(''); }}
            className="text-[#3B82F6] hover:text-[#60A5FA] font-medium transition-colors"
          >
            {isLogin ? 'Sign Up' : 'Log In'}
          </button>
        </p>
      </div>
    </div>
  );

  const renderStep = () => {
    // Login flow
    if (isLogin) {
      if (loginStep === 1) return renderLoginMFA();
      return renderBasicInfo();
    }
    
    // Registration flow - Already verified in-store
    if (signupMethod === 'verified') {
      if (storeVerified && regStep === 1) {
        return renderStoreEmailVerification();
      }
      return renderStoreCodeVerification();
    }
    
    // Registration flow - New customer (or method selection)
    if (signupMethod === null && regStep === 0) {
      return renderSignupMethodSelection();
    }
    
    // Registration flow - New customer steps
    switch (regStep) {
      case 5: return renderComplete();
      case 4: return renderPolling();
      case 3: return renderKYCVerification();
      case 2: return renderAddressForm();
      case 1: return renderSignupEmailVerification();
      default: return renderBasicInfo();
    }
  };

  return (
    <div className="min-h-screen bg-[#001435] flex items-center justify-center px-4 py-12">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md" data-testid="auth-page">
        {/* Inactivity Warning */}
        {inactivityWarning && (
          <div className="mb-4 flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 px-4 py-3 rounded-lg">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">You were logged out due to inactivity. Please sign in again.</span>
            <button 
              onClick={() => setInactivityWarning(false)} 
              className="ml-auto text-amber-400/60 hover:text-amber-400"
            >
              ×
            </button>
          </div>
        )}

        {/* Logo/Branding */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src="https://customer-assets.emergentagent.com/job_forex-compare-2/artifacts/l16i8o9b_FONDER%20CURRENCY%20EXCHANGE-4.png" 
              alt="FonderX Currency Exchange" 
              className="h-14 mx-auto"
            />
          </Link>
          <p className="text-blue-100/60 mt-3">
            {isLogin ? (loginStep === 1 ? 'Verify your login' : 'Welcome back!') : 
              signupMethod === null ? 'Create your account' :
              signupMethod === 'verified' ? (storeVerified ? 'Complete your account' : 'Verify store code') :
              regStep === 0 ? 'Create your account' : 
              regStep === 1 ? 'Verify your email' :
              regStep === 2 ? 'Enter your address' :
              regStep === 3 ? 'Verify your identity' : 
              'Processing...'}
          </p>
        </div>

        {/* Render current step */}
        {renderStep()}

        {/* Academy Promo - only show on initial registration */}
        {!isLogin && regStep === 0 && signupMethod === null && (
          <div className="mt-6 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">New to Crypto?</p>
                <p className="text-blue-100/60 text-xs">
                  Check out our free <Link to="/crypto/academy" className="text-amber-400 hover:underline">FonderX Academy</Link> courses!
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
